import { Client } from '@lemonade-hq/nuiza';
import { produce } from 'immer';
import get from 'lodash/get';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useContext, useMemo, useState } from 'react';

export interface ExternalRequestParams {
  // TODO: add headers
  readonly url: string;
  readonly responseMap?: {
    readonly data: string;
    readonly error: string;
  };
}

interface AsyncDataContextType {
  readonly loadData?: (externalRequestParams: ExternalRequestParams) => Promise<unknown>;
}

export const AsyncDataContext = React.createContext<AsyncDataContextType>({});

type CachedData = Record<string, unknown>;

export const AsyncDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [cachedData, setData] = useState<CachedData>({});
  const loadData = useCallback(
    async ({ url, responseMap }: ExternalRequestParams) => {
      const cachedDataForUrl = cachedData[url];

      if (cachedDataForUrl != null) return cachedDataForUrl;

      try {
        const response = await Client.get(url);

        if (responseMap != null) {
          const error = get(response, responseMap.error) as string | undefined;
          if (error != null) {
            // TODO: currently we're ignoring the error, we should handle it
            throw new Error(error);
          }
        }

        const data = (responseMap != null ? get(response, responseMap.data) : response) as string;

        const newCachedData = produce(cachedData, draft => {
          draft[url] = data;
        });

        setData(newCachedData);
        return data;
      } catch {
        throw new Error(`Couldn't get data from ${url}`);
      }
    },
    [cachedData],
  );

  const contextValue = useMemo(
    () => ({
      loadData,
    }),
    [loadData],
  );

  return <AsyncDataContext.Provider value={contextValue}>{children}</AsyncDataContext.Provider>;
};

export function useAsyncDataProvider(): AsyncDataContextType {
  return useContext(AsyncDataContext);
}
